"use client";

import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { ExternalLink, Copy, MoreHorizontal } from "lucide-react";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { Button } from "@/components/ui/button";
import {
  copyToClipboard,
  isStandalonePwa,
  type InAppBrowserType,
} from "@/lib/utils/in-app-browser";
import { InAppBrowserAppPreview } from "@/components/auth/InAppBrowserAppPreview";
import { useEffect, useState } from "react";

interface InAppBrowserGuideProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  browserType: InAppBrowserType;
}

/**
 * iOS Instagram/Facebook/Threads/Twitter용 풀스크린 가이드 모달.
 * Apple 정책상 코드 강제 이동 불가 → 사용자가 수동으로 Safari로 이동하도록 시각적 안내.
 *
 * 구성:
 * 1. URL 복사 버튼 (자동)
 * 2. 단계별 안내: "우측 상단 ⋯ → '외부 브라우저에서 열기' 또는 'Safari에서 열기' 선택"
 * 3. 이메일 로그인 fallback CTA
 */
export function InAppBrowserGuide({
  open,
  onOpenChange,
  browserType,
}: InAppBrowserGuideProps) {
  const t = useTranslations("common.auth.inAppBrowserGuide");
  const [isPwa, setIsPwa] = useState(false);

  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsPwa(isStandalonePwa());
  }, [open]);

  const handleCopy = async () => {
    const ok = await copyToClipboard(window.location.href);
    if (ok) {
      toast.success(t("copied"));
    } else {
      toast.error(t("copyFailed"));
    }
  };

  // 인앱별 우측 상단 메뉴 명칭 차이 — 사용자 인지 보조
  const menuLabel = (() => {
    switch (browserType) {
      case "instagram":
        return t("menuInstagram");
      case "facebook":
        return t("menuFacebook");
      case "threads":
        return t("menuThreads");
      case "twitter":
        return t("menuTwitter");
      default:
        return t("menuGeneric");
    }
  })();

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("title")}
      description={t("description")}
      drawerHeight="tall"
      className="sm:max-w-md"
      footer={
        <div className="space-y-2 w-full">
          <p className="text-xs text-gray-500">{t("alternativeLabel")}</p>
          <Button
            type="button"
            variant="outline"
            onClick={handleCopy}
            className="w-full"
          >
            <Copy className="mr-2 h-4 w-4" />
            {t("copyLink")}
          </Button>
        </div>
      }
    >
      <div className="space-y-5 pb-2">
        <div className="flex justify-center">
          <InAppBrowserAppPreview
            browserType={browserType}
            className="h-56 w-auto"
            highlightLabel={t("highlightLabel")}
          />
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-900">
            <span className="font-semibold">{t("whyTitle")}</span> {t("whyBody")}
          </p>
        </div>

        {isPwa && (
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
            <span className="font-semibold">{t("pwaNoticeTitle")}</span>{" "}
            {t("pwaNoticeBody")}
          </div>
        )}

        <ol className="space-y-3 text-sm text-gray-800">
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-500 text-xs font-semibold text-white">
              1
            </span>
            <div>
              <p>{t("step1Tap")}</p>
              <p className="mt-1 inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700">
                <MoreHorizontal className="h-3 w-3" />
                {menuLabel}
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-500 text-xs font-semibold text-white">
              2
            </span>
            <div>
              <p>{t("step2Select")}</p>
              <p className="mt-1 inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700">
                <ExternalLink className="h-3 w-3" />
                {t("step2OptionLabel")}
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-500 text-xs font-semibold text-white">
              3
            </span>
            <p>{t("step3Login")}</p>
          </li>
        </ol>
      </div>
    </ResponsiveModal>
  );
}
